import { log } from "../../../";

import * as React from "react";
import { findDOMNode } from "react-dom";
import { observable, action } from "mobx";

import {
    CategoryType,
    CategoryItem,
    VocabularyCategoryItem,
    LessonCategoryItem,
    ImageOverlayType,
    ExpressionCategoryItem,
} from "../../../models";
import { CreateListsStore } from "../../../stores/CreateListsStore";
import { StringUtils } from "../../../core/utils/StringUtils";
import { strings } from "../../../core/localizedStrings";

export interface CategoryItemCardInputBaseProps extends React.Props<any> {
    categoryItem?: CategoryItem;
    store: CreateListsStore;
    onEditCompleteCallback?: (newTitle: string) => void;
}

export class CategoryItemCardInputBase extends React.Component<CategoryItemCardInputBaseProps> {
    @observable
    protected vocabTextFieldValue?: string;
    @observable
    protected questionTextFieldValue?: string = "";
    @observable
    protected answerTextFieldValue?: string = "";
    @observable
    protected incorrect1Value?: string;
    @observable
    protected incorrect2Value?: string;
    @observable
    protected incorrect3Value?: string;
    @observable
    protected enterIncorrectAnswers: boolean = false;

    @observable
    protected useImageAsVocabulary: boolean = true;

    @observable
    protected imageURL?: string;

    @observable
    protected imageOverlay?: ImageOverlayType;

    protected textInput: any;
    protected textInputRef: any;
    protected questionInputRef: any;

    // React component lifecycle events
    // ------------------------------------------
    public componentDidMount() {
        setTimeout(() => {
            this.textInput = findDOMNode(this.textInputRef);
            if (!this.textInput) {
                this.textInput = findDOMNode(this.questionInputRef);
            }

            if (this.textInput) {
                let value = this.textInput.value;
                this.textInput.value = "";
                this.textInput.focus();
                this.textInput.value = value; // Hack to get cursor at the end of text
            }
        }, 500);
    }

    // Render UI
    // ------------------------------------------
    public render() {
        log.error("Should be overridden in base class");
        return null;
    }

    @action
    public componentWillReceiveProps(nextProps: CategoryItemCardInputBaseProps) {
        if (this.textInput) {
            this.textInput.focus();
        }

        if (nextProps.categoryItem && nextProps.categoryItem.text) {
            if (
                nextProps.categoryItem instanceof VocabularyCategoryItem ||
                nextProps.categoryItem instanceof LessonCategoryItem
            ) {
                this.vocabTextFieldValue = nextProps.categoryItem.text.trim();
            }
        } else {
            this.vocabTextFieldValue = "";
        }

        this.imageURL = null;
    }

    // @action
    // public componentWillUpdate(newProps: CategoryItemCardInputBaseProps) {
    //     log.verbose("Component will update.");
    //     let categoryItem = newProps.categoryItem;
    //     if (categoryItem !== undefined && categoryItem.image !== undefined) {
    //         this.getImageURL();
    //     }
    // }

    // UI event handlers
    // ------------------------------------------
    @action
    protected onTextFieldChange = (event: any) => {
        let MAX_LENGTH = 256;
        let value: string = event.currentTarget.value;
        if (value.length > MAX_LENGTH) {
            value = value.slice(0, MAX_LENGTH);
        }
        switch (event.currentTarget.id) {
            case "text":
                this.vocabTextFieldValue = value;
                break;
            case "question":
                this.questionTextFieldValue = value;
                break;
            case "answer":
                this.answerTextFieldValue = value;
                break;
            case "incorrect1":
                this.incorrect1Value = value;
                break;
            case "incorrect2":
                this.incorrect2Value = value;
                break;
            case "incorrect3":
                this.incorrect3Value = value;
                break;
            default:
                break;
        }
    };

    protected onKeyDown = (event: any) => {
        if (event.keyCode === 13) {
            this.onEditComplete();
        }
    };

    @action
    protected onEnterIncorrectRadioChanged = (event: any, value: string) => {
        this.enterIncorrectAnswers = value === "enterIncorrect";
    };

    @action
    protected onEditComplete = () => {
        if (this.validateForType(this.props.categoryItem.type)) {
            this.setCategoryItemPropertiesFromTextFields(this.props.categoryItem);
            this.props.store.updateCategoryItemForCurrentCategory(this.props.categoryItem);
            console.log(this.props.categoryItem);
            if (this.props.categoryItem.type === CategoryType.VocabularyTextOnly) {
                let categoryItem = new VocabularyCategoryItem();
                categoryItem.type = CategoryType.VocabularyTextOnly;
                this.props.store.setCategoryItemToEdit(categoryItem);
            } else if (this.props.categoryItem.type === CategoryType.ExpressionTextOnly) {
                let categoryItem = new ExpressionCategoryItem();
                categoryItem.type = CategoryType.ExpressionTextOnly;
                this.props.store.setCategoryItemToEdit(categoryItem);
            }

            if (this.props.onEditCompleteCallback) {
                this.props.onEditCompleteCallback(this.vocabTextFieldValue);
            }

            this.resetForm();
        }
    };

    protected setCategoryItemPropertiesFromTextFields(categoryItem: CategoryItem) {
        throw Error("Should be implemented in subclass");
    }

    // Mobx actions
    // ------------------------------------------
    @action
    protected validateForType = (type: CategoryType) => {
        switch (type) {
            case CategoryType.Vocabulary:
                if (StringUtils.isEmptyOrUndefined(this.vocabTextFieldValue)) {
                    return false;
                }
                break;

            case CategoryType.VocabularyTextOnly:
                if (StringUtils.isEmptyOrUndefined(this.vocabTextFieldValue)) {
                    this.props.store.shouldShowTextOnlyInput = false;
                    return false;
                }
                break;

            case CategoryType.Lesson:
                if (StringUtils.isEmptyOrUndefined(this.vocabTextFieldValue)) {
                    this.props.store.shouldShowTextOnlyInput = false;
                    return false;
                }
            /* falls through */
            case CategoryType.Expression:
            case CategoryType.ExpressionTextOnly:
                if (
                    StringUtils.isEmptyOrUndefined(this.questionTextFieldValue) &&
                    StringUtils.isEmptyOrUndefined(this.answerTextFieldValue)
                ) {
                    this.props.store.shouldShowTextOnlyInput = false;
                    return false;
                }

                if (
                    StringUtils.isEmptyOrUndefined(this.questionTextFieldValue) ||
                    StringUtils.isEmptyOrUndefined(this.answerTextFieldValue)
                ) {
                    return false;
                }

                if (this.enterIncorrectAnswers === true && StringUtils.isEmptyOrUndefined(this.incorrect1Value)) {
                    this.props.store.rootStore.showInfoMessage({ text: strings.mustHaveOneIncorrectAnswers });
                    return false;
                }
                break;

            default:
                break;
        }
        return true;
    };

    @action
    private resetForm = () => {
        this.vocabTextFieldValue = "";
        this.questionTextFieldValue = "";
        this.answerTextFieldValue = "";
        this.incorrect1Value = "";
        this.incorrect2Value = "";
        this.incorrect3Value = "";

        if (this.textInput) {
            setTimeout(() => {
                this.textInput.focus();
                console.log("Focus!");
            }, 1000);
        }
    };
}
